import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Modal, Select, Spin, Tag, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";

import {
  getFtcUsers,
  getOfficeFtcAccess,
  revokeFtcAssignment,
  saveOfficeFtcAccess,
} from "./controller";

/**
 * Which FTC personnel may correspond with this office, and who they may write to.
 *
 * The office-side view of the same `FtcOfficeAssignment` the FTC Users page edits
 * from the person's side. A super-admin looking at a clinic asks "who covers this
 * practice"; one looking at a person asks "which practices does Alice cover".
 *
 * ⚠️ AN FTC GRANTED THIS OFFICE WITH NO CONTACTS TICKED CAN SEND NOTHING. The
 * server enforces it (crm/services/ftc_mail.py); this panel states it, because
 * the opposite reading — "none listed means all of them" — would turn a
 * half-finished grant into authorisation for the practice's whole contact list.
 *
 * Distinct from the tracked email addresses above it in the same tab: those
 * decide whose mail gets LOGGED against this office, these decide who an FTC may
 * WRITE to.
 */

export default function OfficeFtcAccessPanel({ officeId, contacts, onChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [ftcUsers, setFtcUsers] = useState([]);
  const [editing, setEditing] = useState(null); // {ftcUserId, contactIds, isNew}
  const [saving, setSaving] = useState(false);

  const load = useCallback(
    async function () {
      if (!officeId) return;
      setLoading(true);
      const res = await getOfficeFtcAccess(officeId);
      const ok = res.status >= 200 && res.status < 300 && Array.isArray(res.json);
      // Never render "nobody is assigned" for a failed fetch — an admin would
      // grant access this office may already have.
      setFailed(!ok);
      setRows(ok ? res.json : []);
      setLoading(false);
    },
    [officeId],
  );

  useEffect(function () { load(); }, [load]);

  async function openPicker(existing) {
    if (!ftcUsers.length) {
      const res = await getFtcUsers();
      if (res.status >= 200 && res.status < 300 && Array.isArray(res.json)) {
        setFtcUsers(res.json);
      }
    }
    setEditing(existing
      ? { ftcUserId: existing.ftc_user_id, contactIds: (existing.contacts || []).map((c) => c.id), isNew: false }
      : { ftcUserId: null, contactIds: [], isNew: true });
  }

  async function save() {
    if (!editing || !editing.ftcUserId) return;
    setSaving(true);
    try {
      const res = await saveOfficeFtcAccess(officeId, editing.ftcUserId, editing.contactIds);
      if (res.status >= 200 && res.status < 300) {
        message.success("Access saved.");
        setEditing(null);
        load();
        if (onChange) onChange();
      } else {
        message.error((res.json && res.json.detail) || "Couldn't save that access.");
      }
    } finally {
      setSaving(false);
    }
  }

  function confirmRevoke(row) {
    Modal.confirm({
      zIndex: 10050,
      title: `Remove ${row.ftc_email || "this FTC"}?`,
      content: "They will no longer be able to email anyone at this clinic, and it will disappear from their inbox.",
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async function () {
        const res = await revokeFtcAssignment(row.id);
        if (res.status >= 200 && res.status < 300) {
          load();
          if (onChange) onChange();
        } else {
          message.error("Couldn't remove that access.");
        }
      },
    });
  }

  const assignedIds = rows.map((r) => r.ftc_user_id);
  const pickerOptions = (ftcUsers || [])
    .filter((u) => editing && (!editing.isNew || assignedIds.indexOf(u.id) === -1))
    .map((u) => {
      // `email` is blank on some legacy accounts (the seeded `admin`), which
      // rendered as a bare "— no mailbox yet" with nothing to identify it.
      // _ftc_user_payload already falls back to username, so use that.
      const who = u.name || u.email || `User #${u.id}`;
      return {
        value: u.id,
        label: u.mailbox ? `${who} · ${u.mailbox.mailbox_address}` : `${who} — no mailbox yet`,
      };
    });

  return (
    <div className="corr-section ofa">
      <div className="corr-head">
        <div className="corr-head-text">
          <h4>FTC access</h4>
          <p className="crm-sub">
            Who may email this clinic, and which contacts they may write to.
          </p>
        </div>
        <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => openPicker(null)}>
          Add FTC
        </Button>
      </div>
      {/* Shown while it can still bite — nobody granted yet, or a grant that is
          actually empty. A warning that is always on screen is one people stop
          reading, and this rule matters too much for that. */}
      {!loading && !failed && (!rows.length || rows.some((r) => !(r.contacts || []).length)) ? (
        <p className="corr-caution">
          An FTC granted this clinic with no contacts ticked can send nothing.
        </p>
      ) : null}

      {loading ? (
        <div className="ofa-center"><Spin /></div>
      ) : failed ? (
        <Alert
          type="error"
          showIcon
          message="Couldn't load this clinic's FTC access"
          description="The server returned an error, so what's shown may be incomplete."
        />
      ) : !rows.length ? (
        <div className="crm-empty">
          No FTC is assigned to this clinic, so nobody can correspond with it.
        </div>
      ) : (
        rows.map((r) => (
          <div className="ofa-row" key={r.id}>
            <div className="ofa-row-head">
              <span className="ofa-who">{r.ftc_email || `FTC #${r.ftc_user_id}`}</span>
              <div>
                <Button size="small" type="link" onClick={() => openPicker(r)}>Edit</Button>
                <Button size="small" type="link" danger onClick={() => confirmRevoke(r)}>Remove</Button>
              </div>
            </div>
            {r.contacts && r.contacts.length ? (
              <div className="ofa-contacts">
                {r.contacts.map((c) => (
                  <Tag key={c.id}>{c.name ? `${c.name} · ${c.email}` : c.email}</Tag>
                ))}
              </div>
            ) : (
              <div className="ofa-none">
                No contacts approved — this FTC cannot send anything here.
              </div>
            )}
          </div>
        ))
      )}

      <Modal
        open={!!editing}
        onCancel={() => setEditing(null)}
        title={editing && editing.isNew ? "Give an FTC access to this clinic" : "Edit access"}
        // The office drawer sits at z-index 10001 (global .ant-drawer-open rule in
        // Home/app.css); a modal defaults to 1200 and renders behind its mask —
        // visible but unreachable. Same lift the drawer's other modals use.
        zIndex={10050}
        okText="Save"
        confirmLoading={saving}
        okButtonProps={{ disabled: !(editing && editing.ftcUserId) }}
        onOk={save}
        destroyOnClose
      >
        <div className="ofa-field">
          <label>FTC</label>
          <Select
            showSearch
            optionFilterProp="label"
            value={editing && editing.ftcUserId}
            onChange={(v) => setEditing((e) => ({ ...e, ftcUserId: v }))}
            options={pickerOptions}
            dropdownStyle={{ zIndex: 10060 }}
            disabled={!(editing && editing.isNew)}
            placeholder="Choose an FTC user"
            style={{ width: "100%" }}
          />
        </div>
        <div className="ofa-field">
          <label>Contacts they may email</label>
          {/* A picker over THIS office's contacts, never free text — the server
              re-scopes them on write, and an address nobody approved should not
              be typeable here either. */}
          <Select
            mode="multiple"
            value={(editing && editing.contactIds) || []}
            onChange={(v) => setEditing((e) => ({ ...e, contactIds: v }))}
            dropdownStyle={{ zIndex: 10060 }}
            // A contact with no address is shown but NOT selectable. It used to be
            // offered like any other: ticking one produced a grant the server
            // then dropped (allowed_emails filters blank addresses), so the UI
            // showed an approved contact who could never actually be emailed —
            // and if it was the only one ticked, the clinic silently became
            // unreachable. Better to show why than to hide the person.
            options={(contacts || []).map((c) => {
              const who = c.name ? `${c.name}${c.title ? ` (${c.title})` : ""}` : "";
              return c.email
                ? { value: c.id, label: who ? `${who} · ${c.email}` : c.email }
                : {
                    value: c.id,
                    disabled: true,
                    label: `${who || `Contact #${c.id}`} — no email on file`,
                  };
            })}
            placeholder={(contacts || []).length ? "Choose contacts" : "This clinic has no contacts on file yet"}
            disabled={!(contacts || []).length}
            style={{ width: "100%" }}
          />
          {!(contacts || []).length ? (
            <p className="crm-sub" style={{ marginTop: 6, fontSize: 11.5 }}>
              Add contacts to this clinic in the Overview tab first.
            </p>
          ) : (contacts || []).some((c) => !c.email) ? (
            <p className="crm-sub" style={{ marginTop: 6, fontSize: 11.5 }}>
              Contacts without an email address can&rsquo;t be approved — add one in
              the Overview tab.
            </p>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}
